import { useEffect, useState } from 'react'
import { Control, useWatch, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EarlyPaymentDiscountInfo } from './EarlyPaymentDiscountInfo'

import { Tips } from '@/components/common'
import { ContainerGroupField } from '@/components/common/ContainerGroupField'
import { AutocompleteControl } from '@/components/form-controls'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { Discount, MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'
import { InformationTexts } from '@/types/discounts'
import { AboutYouFormData } from '@/types/forms/'

interface DiscountsControlProps {
    control: Control<AboutYouFormData>
    optionsDiscounts: Discount[]
    watchDiscountType: string
    uniqueDiscountType: boolean
    percentageDiscount: (
        discountType: string,
        optionsDiscounts: Discount[]
    ) => boolean | undefined
    watchRecognizesEthnicGroup: string
    setValue: UseFormSetValue<AboutYouFormData>
    clearErrors: (name?: keyof AboutYouFormData) => void
}

/* eslint-disable max-lines-per-function */
export const Discounts = ({
    control,
    optionsDiscounts,
    watchDiscountType,
    uniqueDiscountType,
    percentageDiscount,
    watchRecognizesEthnicGroup,
    setValue,
    clearErrors
}: DiscountsControlProps): JSX.Element => {
    const { t } = useTranslation()
    const text = 'discounts'

    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const [haveEarlyPayment, setHaveEarlyPayment] = useState<boolean | null>(
        null
    )

    const [discountMessage, setDiscountMessage] = useState<string | null>(null)
    const [informationTexts, setInformationTexts] =
        useState<InformationTexts | null>(null)

    // Watch discountType to verify it exists in current options
    const currentDiscountType = useWatch({
        control,
        name: 'discountType'
    })

    const conditionsMessage = (discount: Discount) => {
        return (
            discount.condiciones?.toUpperCase() === 'AUTOMÁTICO' ||
            discount.categoria === 'No aplica' ||
            discount.condiciones?.toUpperCase() === 'CLASE OYENTE'
        )
    }

    // Clear discountType if it doesn't exist in current options
    useEffect(() => {
        if (
            currentDiscountType &&
            optionsDiscounts.length > 0 &&
            watchRecognizesEthnicGroup
        ) {
            const existsInOptions = optionsDiscounts.some(
                (discount: Discount) =>
                    discount.categoria === currentDiscountType
            )

            if (!existsInOptions) {
                setValue('discountType', '', { shouldValidate: false })
                clearErrors('discountType')
            }
        }
    }, [
        optionsDiscounts,
        currentDiscountType,
        watchRecognizesEthnicGroup,
        setValue,
        clearErrors
    ])

    useEffect(() => {
        const checkPercentageDiscount = () => {
            if (!watchDiscountType) {
                setDiscountMessage(null)

                return
            }

            const discount = percentageDiscount(
                watchDiscountType,
                optionsDiscounts
            )

            if (discount !== undefined) {
                setHaveEarlyPayment(discount)
            }

            // Find the selected discount to check its condition
            const selectedDiscount = optionsDiscounts.find(
                (disc: Discount) => disc.categoria === watchDiscountType
            )

            if (selectedDiscount) {
                // Check if it's uniqueDiscountType OR if condiciones is 'AUTOMATICO'
                const isAutomatic = conditionsMessage(selectedDiscount)

                if (
                    isAutomatic ||
                    import.meta.env.VITE_DISCOUNT_CAMPAIGN_ENABLED
                ) {
                    setDiscountMessage(
                        informationTexts?.automaticDiscountText || ''
                    )
                } else {
                    setDiscountMessage(
                        informationTexts?.nonAutomaticDiscountText || ''
                    )
                }
            } else {
                setDiscountMessage(null)
            }
        }

        checkPercentageDiscount()
    }, [
        watchDiscountType,
        optionsDiscounts,
        percentageDiscount,
        uniqueDiscountType,
        informationTexts
    ])

    useEffect(() => {
        let isMounted = true
        const fetchInformationTexts = async () => {
            if (!isMounted) {
                return
            }
            try {
                const { data, status } =
                    await resourcesApi.getInformationTexts()

                if (status === 200) {
                    setInformationTexts(data)
                }
            } catch (error) {
                console.error('Failed to fetch information texts:', error)
            }
        }

        fetchInformationTexts()

        return () => {
            isMounted = false
        }
    }, [])

    return (
        <section>
            <Tips
                text={t('tips.discounts')}
                title={t('tips.knowIt')}
                banner="news"
                className="mt-8"
            />

            <ContainerGroupField
                tittleGroup={t('forms.groups.discounts')}
                onlyOneField
            >
                <AutocompleteControl
                    key={`discount-${watchRecognizesEthnicGroup}`}
                    name="discountType"
                    control={control}
                    label={t(`${text}.discounts`)}
                    // @ts-expect-error - AutocompleteControl uses labelKey/valueKey to access properties dynamically
                    options={optionsDiscounts}
                    labelKey="text"
                    valueKey="categoria"
                    rules={{
                        required: t(`${text}.required.discount`)
                    }}
                    isDisabled={
                        uniqueDiscountType ||
                        watchRecognizesEthnicGroup === '' ||
                        optionsDiscounts.length === 0
                    }
                    isLoading={
                        (optionsDiscounts === null ||
                            optionsDiscounts.length === 0) &&
                        watchRecognizesEthnicGroup !== ''
                    }
                />
                {watchDiscountType && (
                    <div className="md:grid md:grid-cols-2 md:gap-7">
                        <Tips
                            title={t('discounts.tips.title')}
                            text={discountMessage || ''}
                            banner="tips"
                            className="mt-4"
                        />
                        {haveEarlyPayment && <EarlyPaymentDiscountInfo />}
                    </div>
                )}
            </ContainerGroupField>
        </section>
    )
}
